import {createExerciseModal} from "./exerciseModal";
import {createExerciseSelector} from "./exerciseSelector";
import {buildQuery, findPrefillResponse, getApiUrl, track} from "./exercisePickerShared";
import {bindNativeModalCancel, setNativeModalOpen} from "./modalShared";
import {setupTabs} from "./setupTabs";
import {initStandardsCalculatorForms} from "./standardsCalculatorForm";

function getInitialExercises() {
    return findPrefillResponse("/api/exercises");
}

function cleanUrl(url) {
    return (url || "").replace(/\/$/, "");
}

function initInlineExerciseSelectors() {
    const selectors = document.querySelectorAll("[data-exercise-selector]");
    const initialExercises = getInitialExercises();

    for (let i = 0; i < selectors.length; i++) {
        if (selectors[i].dataset.exerciseSelectorInitialized === "true") {
            continue;
        }
        selectors[i].dataset.exerciseSelectorInitialized = "true";
        createExerciseSelector({
            root: selectors[i],
            initialExercises: initialExercises,
        });
    }
}

function initStandardsExerciseSelector() {
    const root = document.querySelector("[data-standards-exercise-selector]");
    if (!root) {
        return;
    }

    const modalRoot = document.getElementById(root.dataset.modalId);
    const currentExerciseId = String(root.dataset.currentExerciseId || "");
    const currentExerciseUrl = root.dataset.currentExerciseUrl || "";
    const baseUrl = cleanUrl(root.dataset.baseUrl);
    const openInput = root.querySelector("[data-standards-exercise-open]");
    const compareButton = root.querySelector("[data-standards-exercise-compare]");
    let isComparison = false;

    const modal = createExerciseModal({
        root: modalRoot,
        initialExercises: getInitialExercises(),
        onSelect: function (exercise) {
            const selectedExerciseId = String(exercise.id || "");
            if (isComparison) {
                if (currentExerciseId !== selectedExerciseId) {
                    window.location = baseUrl + "/" + currentExerciseUrl + "-vs-" + exercise.name_url;
                }
                return;
            }
            window.location = baseUrl + "/" + exercise.name_url;
        },
    });

    function openModal(nextIsComparison) {
        isComparison = nextIsComparison;
        modal.showModal();
    }

    if (openInput) {
        openInput.addEventListener("click", function () {
            openModal(false);
        });
    }
    if (compareButton) {
        compareButton.addEventListener("click", function () {
            openModal(true);
        });
    }
}

function initStandardsComparisonSelector() {
    const root = document.querySelector("[data-standards-comparison-selector]");
    if (!root) {
        return;
    }

    const modalRoot = document.getElementById(root.dataset.modalId);
    const exercise1 = {
        id: String(root.dataset.exercise1Id || ""),
        name_url: root.dataset.exercise1Url || "",
    };
    const exercise2 = {
        id: String(root.dataset.exercise2Id || ""),
        name_url: root.dataset.exercise2Url || "",
    };
    const urlTemplate = root.dataset.urlTemplate || "";
    let selectedSide = "exercise1";

    function getComparisonUrl(nextExercise1, nextExercise2) {
        return urlTemplate
            .replace("{exercise1}", nextExercise1.name_url)
            .replace("{exercise2}", nextExercise2.name_url);
    }

    const modal = createExerciseModal({
        root: modalRoot,
        initialExercises: getInitialExercises(),
        onSelect: function (exercise) {
            const selectedExercise = {
                id: String(exercise.id || ""),
                name_url: exercise.name_url,
            };

            if (selectedSide === "exercise2") {
                if (exercise1.id !== selectedExercise.id) {
                    window.location = getComparisonUrl(exercise1, selectedExercise);
                }
                return;
            }

            if (selectedExercise.id !== exercise2.id) {
                window.location = getComparisonUrl(selectedExercise, exercise2);
            }
        },
    });

    const openNodes = root.querySelectorAll("[data-standards-comparison-open]");
    for (let i = 0; i < openNodes.length; i++) {
        openNodes[i].addEventListener("click", function () {
            selectedSide = openNodes[i].dataset.standardsComparisonOpen || "exercise1";
            modal.showModal();
        });
    }
}

function setModalOpen(modal, open) {
    setNativeModalOpen(modal, open);
}

function createTextElement(tagName, className, text) {
    const element = document.createElement(tagName);
    if (className) {
        element.className = className;
    }
    element.textContent = text;
    return element;
}

function appendSummaryTag(parent, label, value) {
    if (!value) {
        return;
    }

    const control = document.createElement("div");
    control.className = "control";
    const tags = document.createElement("div");
    tags.className = "tags has-addons are-medium";
    tags.appendChild(createTextElement("span", "tag is-primary", label));
    tags.appendChild(createTextElement("span", "tag", value));
    control.appendChild(tags);
    parent.appendChild(control);
}

function hasHighlight(table, rowIndex, cellIndex) {
    const highlights = table.highlights || [];
    for (let i = 0; i < highlights.length; i++) {
        if (highlights[i][0] === rowIndex && highlights[i][1] === cellIndex) {
            return true;
        }
    }
    return false;
}

function renderTable(root, table) {
    root.innerHTML = "";

    const container = document.createElement("div");
    container.className = "table-container";

    const tableNode = document.createElement("table");
    tableNode.className = "table is-fullwidth is-striped is-narrow";
    if (table.alignment) {
        tableNode.className += " has-text-" + table.alignment;
    }

    const thead = document.createElement("thead");
    const headerRow = document.createElement("tr");
    const headers = table.headers || [];
    for (let i = 0; i < headers.length; i++) {
        const th = document.createElement("th");
        const alignment = table.alignments && table.alignments[i];
        if (alignment && alignment !== (table.alignment || "left")) {
            th.className = "has-text-" + alignment;
        }
        if (table.widths && table.widths[i]) {
            th.style.width = table.widths[i] + "%";
        }
        if (table.abbreviations && table.abbreviations[i]) {
            const abbr = document.createElement("abbr");
            abbr.title = headers[i];
            abbr.appendChild(createTextElement("span", "", table.abbreviations[i]));
            th.appendChild(abbr);
        } else {
            th.textContent = headers[i];
        }
        headerRow.appendChild(th);
    }
    thead.appendChild(headerRow);
    tableNode.appendChild(thead);

    const tbody = document.createElement("tbody");
    const body = table.body || [];
    for (let rowIndex = 0; rowIndex < body.length; rowIndex++) {
        const row = document.createElement("tr");
        for (let cellIndex = 0; cellIndex < body[rowIndex].length; cellIndex++) {
            const td = document.createElement("td");
            const alignment = table.alignments && table.alignments[cellIndex];
            let className = "";
            if (alignment && alignment !== (table.alignment || "left")) {
                className = "has-text-" + alignment;
            }
            if (hasHighlight(table, rowIndex, cellIndex)) {
                className += (className ? " " : "") + "has-background-tablehighlight";
            }
            if (className) {
                td.className = className;
            }
            td.textContent = body[rowIndex][cellIndex];
            row.appendChild(td);
        }
        tbody.appendChild(row);
    }
    tableNode.appendChild(tbody);
    container.appendChild(tableNode);
    root.appendChild(container);
}

function initStandardsCreate(root) {
    const inputModal = root.querySelector("[data-standards-create-input-modal]");
    const tableModal = root.querySelector("[data-standards-create-table-modal]");
    const openButtons = document.querySelectorAll("[data-standards-create-open]");
    const submitButton = root.querySelector("[data-standards-create-submit]");
    const backButton = root.querySelector("[data-standards-create-back]");
    const closeNodes = root.querySelectorAll("[data-standards-create-close]");
    const genderSelect = root.querySelector("[data-standards-create-gender]");
    const ageInput = root.querySelector("[data-standards-create-age]");
    const bodyweightInput = root.querySelector("[data-standards-create-bodyweight]");
    const errorNode = root.querySelector("[data-standards-create-error]");
    const tableNode = root.querySelector("[data-standards-create-table]");
    const summaryNode = root.querySelector("[data-standards-create-summary]");

    function showInputModal(event) {
        const gender = event.currentTarget.getAttribute("data-gender");
        if (genderSelect && (gender === "male" || gender === "female")) {
            genderSelect.value = gender;
        }
        setModalOpen(tableModal, false);
        setModalOpen(inputModal, true);
    }

    function hideModals() {
        setModalOpen(inputModal, false);
        setModalOpen(tableModal, false);
    }

    function showError(message) {
        if (errorNode) {
            errorNode.textContent = message;
            errorNode.classList.remove("is-hidden");
        }
    }

    function clearError() {
        if (errorNode) {
            errorNode.textContent = "";
            errorNode.classList.add("is-hidden");
        }
    }

    function renderSummary(gender, age, bodyweight) {
        summaryNode.innerHTML = "";
        appendSummaryTag(summaryNode, root.dataset.exerciseText || "Exercise", root.dataset.exerciseName);
        appendSummaryTag(summaryNode, root.dataset.genderText || "Gender", gender === "female" ? root.dataset.femaleText : root.dataset.maleText);
        appendSummaryTag(summaryNode, root.dataset.ageText || "Age", age);
        appendSummaryTag(summaryNode, root.dataset.bodyweightText || "Bodyweight", bodyweight ? bodyweight + " " + root.dataset.unit : "");
    }

    function createStandards() {
        const age = ageInput ? ageInput.value : "";
        const bodyweight = bodyweightInput ? bodyweightInput.value : "";
        const gender = genderSelect ? genderSelect.value : root.dataset.gender;

        clearError();
        submitButton.disabled = true;
        submitButton.classList.add("is-loading");
        track("standard-action", "standard-modal", "create");

        const params = {
            exercise_id: root.dataset.exerciseId,
            gender: gender,
            bodyweights: bodyweight ? bodyweight : undefined,
            ages: age ? age : undefined,
            unit: root.dataset.unit,
            row: age || !bodyweight ? "bodyweight" : "age",
            value: root.dataset.exerciseType === "Bodyweight/Time"
                ? "time"
                : (root.dataset.exerciseType === "Bodyweight/Reps" ? "reps" : "weight"),
        };

        fetch(getApiUrl("standard-table-definitions") + "?" + buildQuery(params), {
            headers: {
                "Accept": "application/json",
            },
            credentials: "same-origin",
        }).then(function (response) {
            return response.json().then(function (json) {
                if (!response.ok) {
                    const error = new Error("Unable to create standards");
                    error.response = json;
                    throw error;
                }
                return json;
            });
        }).then(function (json) {
            renderSummary(gender, age, bodyweight);
            renderTable(tableNode, json.data[0]);
            setModalOpen(inputModal, false);
            setModalOpen(tableModal, true);
        }).catch(function (error) {
            const errorData = error.response || {};
            let message = root.dataset.unableText || "Unable to create standards";
            if (errorData && errorData.type) {
                message += ": " + (errorData.detail || errorData.title || root.dataset.unknownErrorText || "Unknown error");
            }
            showError(message);
        }).then(function () {
            submitButton.disabled = false;
            submitButton.classList.remove("is-loading");
        });
    }

    for (let i = 0; i < openButtons.length; i++) {
        openButtons[i].addEventListener("click", showInputModal);
    }
    if (submitButton) {
        submitButton.addEventListener("click", createStandards);
    }
    if (backButton) {
        backButton.addEventListener("click", showInputModal);
    }
    for (let i = 0; i < closeNodes.length; i++) {
        closeNodes[i].addEventListener("click", hideModals);
    }
    bindNativeModalCancel(inputModal, hideModals);
    bindNativeModalCancel(tableModal, hideModals);
}

function initStandardsCreateForms() {
    const roots = document.querySelectorAll("[data-standards-create]");
    for (let i = 0; i < roots.length; i++) {
        initStandardsCreate(roots[i]);
    }
}

function initStandardsPage() {
    setupTabs();
    initStandardsCalculatorForms();
    initInlineExerciseSelectors();
    initStandardsExerciseSelector();
    initStandardsComparisonSelector();
    initStandardsCreateForms();
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initStandardsPage);
} else {
    initStandardsPage();
}
